import {
  Business,
  BusinessInvitation,
  BusinessMembership,
  LeaveBusinessResult,
} from '../types';
import { delay } from './delay';
import {
  db,
  findActiveBusinessByUserId,
  findActiveMembershipByUserId,
  findBusinessById,
  findBuyerAccountByUserId,
} from './db';
import { ERR_NOT_FOUND, ERR_VALIDATION } from './errors';
import { readUserIdFromToken } from './auth-utils';
import {
  CreateBusinessRequest,
  CreateInvitationRequest,
  UpdateBusinessRequest,
} from '../services/businessService';

const emptyAddress = (): Business['billingAddress'] => ({
  line1: '',
  suburb: '',
  state: '',
  postcode: '',
  country: 'AU',
});

const requireActiveMembership = (userId: string): BusinessMembership => {
  const membership = findActiveMembershipByUserId(userId);
  if (!membership) throw ERR_NOT_FOUND('Active membership');
  return membership;
};

const requireOwner = (userId: string): BusinessMembership => {
  const membership = requireActiveMembership(userId);
  if (membership.role !== 'OWNER') throw ERR_VALIDATION('Owner only');
  return membership;
};

const issueToken = (suffix: string) =>
  `mock.${suffix}.${Date.now().toString(36)}`;

const sevenDaysFromNow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString();
};

const decorateMembership = (m: BusinessMembership): BusinessMembership => {
  const account = findBuyerAccountByUserId(m.userId);
  return {
    ...m,
    user: account
      ? { fullName: account.fullName, email: account.email }
      : undefined,
  };
};

export const businessMock = {
  get: async (): Promise<Business | null> => {
    await delay(80, 200);
    const userId = readUserIdFromToken();
    const found = findActiveBusinessByUserId(userId);
    if (!found) return null;
    return { ...found.business };
  },

  create: async (req: CreateBusinessRequest): Promise<Business> => {
    await delay();
    const userId = readUserIdFromToken();
    if (findActiveMembershipByUserId(userId)) {
      throw ERR_VALIDATION('Already in a Business');
    }
    const business: Business = {
      id: `biz-${Date.now().toString(36)}`,
      name: req.name,
      billingAddress: emptyAddress(),
      shippingAddress: emptyAddress(),
      status: 'ACTIVE',
      ownerUserId: userId,
    };
    db.businesses.push(business);
    db.memberships.push({
      id: `mem-${Date.now().toString(36)}`,
      businessId: business.id,
      userId,
      role: 'OWNER',
      status: 'ACTIVE',
      joinedAt: new Date().toISOString(),
    });
    return { ...business };
  },

  update: async (patch: UpdateBusinessRequest): Promise<Business> => {
    await delay();
    const userId = readUserIdFromToken();
    const membership = requireOwner(userId);
    const business = findBusinessById(membership.businessId);
    if (!business) throw ERR_NOT_FOUND('Business');
    Object.assign(business, patch);
    return { ...business };
  },

  listMembers: async (): Promise<BusinessMembership[]> => {
    await delay(60, 180);
    const userId = readUserIdFromToken();
    const membership = requireActiveMembership(userId);
    return db.memberships
      .filter(
        (m) =>
          m.businessId === membership.businessId && m.status === 'ACTIVE',
      )
      .map(decorateMembership);
  },

  removeMember: async (targetUserId: string): Promise<void> => {
    await delay();
    const userId = readUserIdFromToken();
    const ownerMembership = requireOwner(userId);
    if (targetUserId === userId)
      throw ERR_VALIDATION('Owner cannot remove self');
    const target = db.memberships.find(
      (m) =>
        m.businessId === ownerMembership.businessId &&
        m.userId === targetUserId &&
        m.status === 'ACTIVE',
    );
    if (!target) throw ERR_NOT_FOUND('Membership');
    target.status = 'REMOVED';
    target.removedAt = new Date().toISOString();
  },

  listInvitations: async (): Promise<BusinessInvitation[]> => {
    await delay(60, 180);
    const userId = readUserIdFromToken();
    const membership = requireOwner(userId);
    return db.invitations
      .filter((i) => i.businessId === membership.businessId)
      .map((i) => ({ ...i }));
  },

  createInvitation: async (
    req: CreateInvitationRequest,
  ): Promise<BusinessInvitation> => {
    await delay();
    const userId = readUserIdFromToken();
    const membership = requireOwner(userId);
    const email = req.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      throw ERR_VALIDATION('Invalid email');
    const inv: BusinessInvitation = {
      id: `inv-${Date.now().toString(36)}`,
      businessId: membership.businessId,
      invitedEmail: email,
      invitedByUserId: userId,
      token: `inv-token-${Date.now().toString(36)}`,
      expiresAt: sevenDaysFromNow(),
      status: 'PENDING',
      createdAt: new Date().toISOString(),
    };
    db.invitations.push(inv);
    return { ...inv };
  },

  revokeInvitation: async (invitationId: string): Promise<void> => {
    await delay();
    const userId = readUserIdFromToken();
    const membership = requireOwner(userId);
    const inv = db.invitations.find(
      (i) => i.id === invitationId && i.businessId === membership.businessId,
    );
    if (!inv) throw ERR_NOT_FOUND('Invitation');
    if (inv.status !== 'PENDING') throw ERR_VALIDATION('Invitation not pending');
    inv.status = 'REVOKED';
  },

  leave: async (): Promise<LeaveBusinessResult> => {
    await delay();
    const userId = readUserIdFromToken();
    const membership = requireActiveMembership(userId);
    if (membership.role === 'OWNER') {
      const otherActiveOwners = db.memberships.some(
        (m) =>
          m.businessId === membership.businessId &&
          m.role === 'OWNER' &&
          m.status === 'ACTIVE' &&
          m.userId !== userId,
      );
      if (!otherActiveOwners)
        throw ERR_VALIDATION('CANNOT_LEAVE_AS_SOLE_OWNER');
    }
    membership.status = 'LEFT';
    membership.removedAt = new Date().toISOString();
    return {
      leftBusinessId: membership.businessId,
      newToken: {
        accessToken: issueToken(userId),
        refreshToken: issueToken(userId + '.refresh'),
        expiresIn: 60 * 60,
      },
    };
  },
};
